import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "~/components/PageHeader";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import z from "zod";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { authClient, createAuthError } from "~/lib/better-auth/client";
import { ChangePasswordSchema } from "~/lib/zod-schemas/auth.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { CheckCircle, LoaderIcon } from "lucide-react";
import { Label } from "~/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";

export const Route = createFileRoute("/dashboard/password")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof ChangePasswordSchema>>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: {
      password: "",
      newPassword: "",
      passwordRepeat: "",
    },
  });

  const changePassword = async (data: ChangePasswordSchema) => {
    const { data: response, error } = await authClient.changePassword({
      newPassword: data.newPassword,
      currentPassword: data.password,
      revokeOtherSessions: true,
    });
    if (error) {
      throw createAuthError(error, "An error occurred while changing password. Please try again.", "password-change");
    }
    return response;
  };

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
  const { isPending: isLoading, error, isSuccess } = changePasswordMutation;

  const handleSubmit = async (values: z.infer<typeof ChangePasswordSchema>) => {
    try {
      await changePasswordMutation.mutateAsync(values);
      form.reset();
    } catch (err) {
      console.error("Caught mutation error:", err);
    }
  };
  return (
    <div className="w-full mb-6">
      <PageHeader title="Passwort ändern" />
      {isSuccess ? (
        <Alert variant={"default"} className="mb-6">
          <CheckCircle />
          <AlertTitle>Passwort erfolgreich geändert</AlertTitle>
          <AlertDescription>
            Dein Passwort wurde erfolgreich geändert. Du kannst dich nun mit
            deinem neuen Passwort anmelden.
          </AlertDescription>
        </Alert>
      ) : null}
      {error && (
        <Alert variant={"destructive"} className="mb-6">
          <CheckCircle />
          <AlertTitle>Fehler beim Ändern des Passworts</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col items-start gap-6 w-full"
          noValidate
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Aktuelles Passwort bestätigen</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="password"
                    name="password"
                    type="password"
                    maxLength={50}
                    placeholder="Aktuelles Passwort"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Neues Passwort eingeben</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    maxLength={50}
                    placeholder="Neues Passwort"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordRepeat"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Input
                    {...field}
                    id="password-repeat"
                    name="passwordRepeat"
                    type="password"
                    maxLength={50}
                    placeholder="Neues Passwort bestätigen"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />
          <div className="flex justify-end gap-3 w-full mt-4">
            <Button type="reset" variant={"outline"}>
              Abbrechen
            </Button>
            <Button type="submit" variant="default">
              {isLoading ? (
                <>
                  <LoaderIcon className="animate-spin" />
                  Loading...
                </>
              ) : (
                "Speichern"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
